import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Label } from 'reactstrap';
import { enabledSelector, labelSelector, newPathSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import SignatureInput from '../../common/controls/signatureInput';
import logValueOnChange from '../commonActions/logValueOnChange';
import uploadUtils from '../../../utils/uploadUtils';
const SAVED_SIGNATURE_ERROR = 'There was a problem loading the saved signature.';

// Sits inside the signature box rather than above it, so the field shows one signature either
// way. The canvas is transparent and painted over the top of this, so strokes drawn across an
// applied signature stay visible and the canvas still receives them. Dimensions match the stage
// in `signature.jsx`, inset by the 1px border `.signature-input` draws.
const appliedSignatureStyle = {
  height: '172px',
  left: '1px',
  objectFit: 'contain',
  pointerEvents: 'none',
  position: 'absolute',
  top: '1px',
  width: '656px',
  zIndex: 0
};

/**
 *  Signs for the current user, in place.
 *
 *  Mirrors the "Use Saved Signature" button from `signatureModal`, rendered inline instead of
 *  in its own modal: the approve action already opens a modal, and a second modal cannot be
 *  drawn in on top of one that is already open.
 *
 *  The canvas is always on screen and always live, so the approver can re-sign at any point and
 *  is never left holding a signature they cannot change. A signature the canvas cannot draw
 *  itself -- one pulled from the profile, or one already on the record when the field mounts --
 *  is shown inside the canvas rather than as a second box above it.
 */
const InlineSignatureField = ({
  enabled,
  label: _label = 'Signature',
  notificationActions,
  onChange,
  path,
  testingIdentifier: _testingIdentifier = null,
  user: _user = null,
  value: _value = null
}) => {
  const savedSignaturePath = (_user === null || _user === void 0 ? void 0 : _user.signaturePath) || null;
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  // Whether to preview a signature the canvas cannot draw itself. Content alone cannot decide
  // this: the canvas holds its strokes in its own state, and the write a stroke triggers lands on
  // its own schedule, so a preview driven by arriving content would replace the canvas under a
  // half-finished signature and throw those strokes away.
  const {
    _cache: appliedSignatureUrl = null
  } = _value || {};
  const [showAppliedSignature, setShowAppliedSignature] = useState(Boolean(appliedSignatureUrl));
  const isPreviewingApplied = showAppliedSignature && Boolean(appliedSignatureUrl);
  const applySignature = file => {
    if (!file) {
      onChange({
        path,
        value: null,
        displayValue: 'removed signature'
      });
      return;
    }
    onChange({
      path,
      // `_cache` renders the signature until the record is saved. The server strips it on
      // save and attaches a fresh signed url whenever the content is read back.
      value: {
        _cache: file.cachedUrl,
        _download: file.fileName
      },
      displayValue: file.fileName
    });
  };

  // Drawing over an applied signature replaces it, and clearing the canvas clears it, so either
  // way the canvas takes over showing what is on the record from here on.
  const handleDrawn = file => {
    setShowAppliedSignature(false);
    applySignature(file);
  };
  const handleUseSavedSignature = async () => {
    setIsLoadingSaved(true);
    try {
      const cachedUrl = await uploadUtils.getS3DownloadUrl(savedSignaturePath);
      applySignature({
        fileName: savedSignaturePath,
        cachedUrl
      });
      setShowAppliedSignature(true);
    } catch (_unused) {
      notificationActions.error({
        message: SAVED_SIGNATURE_ERROR
      });
    } finally {
      setIsLoadingSaved(false);
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": _testingIdentifier,
    className: _testingIdentifier
  }, /*#__PURE__*/React.createElement(Label, {
    style: {
      display: 'block'
    }
  }, _label), savedSignaturePath ? /*#__PURE__*/React.createElement(Button, {
    block: true,
    className: "t-isf-use-saved",
    color: "secondary",
    "data-testid": "t-isf-use-saved",
    disabled: !enabled || isLoadingSaved,
    onClick: handleUseSavedSignature,
    style: {
      marginBottom: '15px'
    }
  }, isLoadingSaved ? 'Loading...' : 'Use Saved Signature') : null, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative'
    }
  }, isPreviewingApplied ? /*#__PURE__*/React.createElement("img", {
    alt: "signature",
    className: "t-isf-applied",
    "data-testid": "t-isf-applied",
    src: appliedSignatureUrl,
    style: appliedSignatureStyle
  }) : null, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      zIndex: 1
    }
  }, /*#__PURE__*/React.createElement(SignatureInput, {
    disabled: !enabled || isLoadingSaved,
    hasAppliedSignature: isPreviewingApplied,
    onChange: handleDrawn
  }))));
};
InlineSignatureField.propTypes = {
  enabled: PropTypes.bool.isRequired,
  label: PropTypes.string,
  notificationActions: PropTypes.shape({
    error: PropTypes.func
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  testingIdentifier: PropTypes.string,
  user: PropTypes.shape({
    signaturePath: PropTypes.string
  }),
  value: PropTypes.shape({
    _cache: PropTypes.string,
    _download: PropTypes.string
  })
};
export { InlineSignatureField as UncomposedInlineSignatureField };
export default entityComposition(InlineSignatureField, [], {
  enabled: enabledSelector,
  label: labelSelector,
  onChange: logValueOnChange,
  path: newPathSelector,
  user: state => {
    var _state$login;
    return (_state$login = state.login) === null || _state$login === void 0 ? void 0 : _state$login.user;
  },
  value: valueSelector
}, ['notification']);